/* ═══════════════════════════════════════════════════════════
   ASTROITE — Language context / provider

   <LangProvider> wraps the app once (in index.js). Any component
   calls useLang() to get { lang, setLang, t }.

     const { t, lang, setLang } = useLang();
     t('peeths.title', { total: 108 })   // interpolates {total}
     t('game.dialogue')                  // returns the array as-is

   t() never throws: a missing key falls back to English, then to
   the key string itself (visible but non-breaking) — so dev and
   prod behave the same even with an incomplete table.
   ═══════════════════════════════════════════════════════════ */

import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { LANGS, isLang, getStoredLang, setStoredLang } from './config';
import { STRINGS } from './strings';

const LangContext = createContext(null);

// Replace {placeholders} with provided values; unknown placeholders are left intact.
function interpolate(template, vars) {
  if (!vars || typeof template !== 'string') return template;
  return template.replace(/\{(\w+)\}/g, (match, key) =>
    vars[key] != null ? String(vars[key]) : match
  );
}

// Resolve a dot-path ("peeths.modalNumber") to its { hi, mr, en } leaf.
function resolveLeaf(path) {
  const parts = path.split('.');
  let node = STRINGS;
  for (let i = 0; i < parts.length; i++) {
    if (node == null || typeof node !== 'object') return undefined;
    node = node[parts[i]];
  }
  return node;
}

export function LangProvider({ children }) {
  // Lazy initialiser → reads persisted choice exactly once, env-safe.
  const [lang, setLangState] = useState(getStoredLang);

  // Accepts a value or an updater fn; validates and persists.
  const setLang = useCallback((next) => {
    setLangState((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      if (!isLang(resolved) || resolved === prev) return prev;
      setStoredLang(resolved);
      return resolved;
    });
  }, []);

  // Keep the document language in sync for accessibility / screen readers.
  useEffect(() => {
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const t = useCallback((path, vars) => {
    const leaf = resolveLeaf(path);
    if (leaf == null || typeof leaf !== 'object') {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(`[i18n] missing string for key: "${path}"`);
      }
      return path; // visible but harmless fallback
    }
    let value = leaf[lang];
    if (value == null) value = leaf.en != null ? leaf.en : leaf[LANGS[0]];
    // Arrays (e.g. dialogue lines) and other non-strings pass through untouched.
    return typeof value === 'string' ? interpolate(value, vars) : value;
  }, [lang]);

  const ctx = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <LangContext.Provider value={ctx}>{children}</LangContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useLang() must be used inside <LangProvider>');
  return ctx;
}
